import React from "react";
import { captureError } from "@shared/lib/monitoring/sentry";

interface Props {
    children: React.ReactNode;
}

interface State {
    error: Error | null;
}

/**
 * lazy 청크 로드 실패(배포로 청크 해시가 바뀌어 옛 탭이 옛 청크를 404 받는 등)를 감지한다.
 */
function isChunkLoadError(error: Error): boolean {
    const name = error?.name ?? "";
    const message = error?.message ?? "";
    return (
        name === "ChunkLoadError" ||
        /Loading chunk [\d]+ failed/i.test(message) ||
        /Loading CSS chunk/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message)
    );
}

/**
 * 라우트(lazy) 영역 에러 바운더리.
 * - 청크 로드 실패: 새로고침 안내(새 배포 반영). 화면이 깨지지 않게 graceful 처리.
 * - 그 외 에러: re-throw 하여 기존 동작(상위로 전파)을 보존한다 — 실제 버그를 숨기지 않는다.
 */
class RouteErrorBoundary extends React.Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        if (isChunkLoadError(error)) {
            // 청크 로드 실패는 여기서 graceful 처리(삼킴)하므로, telemetry 가 남으려면 직접 캡처해야 한다.
            console.error("라우트 청크 로드 실패:", error);
            captureError(error, { kind: "chunk-load", componentStack: info?.componentStack });
        } else {
            // 비-청크 에러는 render 에서 re-throw → 상위 라우트 errorElement(RouteError)가 캡처한다.
            // 여기선 로그만 남겨 중복 캡처를 피한다. [E3]
            console.error("라우트 렌더 에러(상위 errorElement 로 전파):", error);
        }
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        const { error } = this.state;
        if (error) {
            // 청크 로드 실패가 아니면 기존 동작 보존(상위로 전파)
            if (!isChunkLoadError(error)) throw error;
            return (
                <div style={{ padding: 24, textAlign: "center" }}>
                    <p>페이지를 불러오지 못했습니다. 새 버전이 배포되었을 수 있어요.</p>
                    <button
                        onClick={this.handleReload}
                        style={{ marginTop: 12, height: 36, padding: "0 16px", cursor: "pointer" }}
                    >
                        새로고침
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

export default RouteErrorBoundary;
